import { getSubmissionsByAssignmentId } from '../../../service';

// A committed bulk job's commit reply (06 §5.1's 201 body), for the paths that
// arrive at a COMMITTED job without having made the commit themselves: a
// resumed draft, another tab's commit, or a commit the server's stalled-commit
// reconciler finished.
//
// 06 §3.2 has the job view carry `commit` {state, batchId, submissionIds,
// students[{scriptId, studentId, submissionId, pageIds}], needsExtraction,
// preExtracted, dropped}. When a view comes without it, the reply is rebuilt
// from the assignment's submissions and the job's own scripts:
// - on a class task, only the rows of students the job's scripts name (the
//   task can hold rows this upload never made);
// - on a My Drafts document, every row of the document (a document pinned to
//   v2 has no rows from any other upload).
// A rebuilt reply asks for no extraction (it can't tell which answers were
// read in Check pages); Review's own extraction state covers any that weren't.
// Only a COMMITTED job hands a reply on: a reopened job is READY while its
// `commit` still says COMMITTED (06 §4.3), and Check pages owns it again.

/** A commit reply from the job's own `commit` record; null when the view has none. */
export const replyFromJob = (job) => {
  const commit = job?.commit;
  if (commit?.state !== 'COMMITTED') return null;
  return {
    batchId: commit.batchId ?? null,
    submissionIds: Array.isArray(commit.submissionIds)
      ? commit.submissionIds
      : [],
    students: Array.isArray(commit.students) ? commit.students : [],
    preExtracted: commit.preExtracted ?? 0,
    needsExtraction: Array.isArray(commit.needsExtraction)
      ? commit.needsExtraction
      : [],
    dropped: commit.dropped ?? null,
  };
};

/**
 * The reply rebuilt from the assignment's rows (see above). Pure.
 * @param {{rows: Array, ws: object|null, scopeToScripts: boolean}} args
 * @returns {object|null} null when no row belongs to the job
 */
export const replyFromSubmissions = ({ rows, ws, scopeToScripts }) => {
  const list = (Array.isArray(rows) ? rows : []).filter((row) => row?.id);
  const byStudent = new Map(
    list
      .filter((row) => row.studentId)
      .map((row) => [String(row.studentId), row])
  );
  const students = (ws?.scriptOrder || Object.keys(ws?.scripts || {}))
    .map((scriptId) => [scriptId, ws?.scripts?.[scriptId]])
    .filter(([, script]) => script?.studentId)
    .flatMap(([scriptId, script]) => {
      const row = byStudent.get(String(script.studentId));
      return row
        ? [
            {
              scriptId,
              studentId: String(script.studentId),
              submissionId: row.id,
            },
          ]
        : [];
    });
  const ids = scopeToScripts
    ? students.map((entry) => entry.submissionId)
    : list.map((row) => row.id);
  const submissionIds = [...new Set(ids)];
  if (!submissionIds.length) return null;
  return {
    batchId: null,
    submissionIds,
    students,
    preExtracted: 0,
    needsExtraction: [],
    dropped: null,
  };
};

/**
 * The commit reply for a COMMITTED job: its own `commit` when the view has it,
 * else rebuilt from the assignment's rows. Rejects when the rows can't be read.
 * @param {{job: object, ws: object|null, assignmentId: string,
 *   scopeToScripts: boolean}} args
 * @returns {Promise<object|null>} null when the job isn't COMMITTED or no row is its
 */
export const resolveCommittedReply = async ({
  job,
  ws,
  assignmentId,
  scopeToScripts,
}) => {
  if (job?.status !== 'COMMITTED') return null;
  const fromJob = replyFromJob(job);
  if (fromJob) return fromJob;
  if (!assignmentId) return null;
  const rows = await getSubmissionsByAssignmentId(assignmentId, {
    requireValidResponse: true,
  });
  return replyFromSubmissions({ rows, ws, scopeToScripts });
};
